import { CodedProperty, fluentValidator, stringArraysEqual, ValidationResult } from "sprint-common-web";

import { isTrailerVersionType } from "../../../../common/types/TrailerContentVersion";
import {
    ContentAssociationTvEpisodeDetails,
    ContentAssociationTvEpisodeVersionDetails
} from "../types/ContentAssociationDetails";

export const createContentAssociationTvSeriesItemValidator = () => {
    const validateEpisode = (content: ContentAssociationTvEpisodeDetails): ValidationResult => {
        return fluentValidator<ContentAssociationTvEpisodeDetails>()
            .when((episode) => !!episode.buzz?.isArchived)
            .addWarning("Episode is archived in Buzz")
            .when((episode) => !!episode.buzz?.isDeleted)
            .addWarning("Episode is deleted in Buzz")
            .validate(content);
    };

    const validateVersion = (
        versionToValidate: ContentAssociationTvEpisodeVersionDetails,
        allVersions: ContentAssociationTvEpisodeVersionDetails[]
    ): ValidationResult => {
        return fluentValidator<ContentAssociationTvEpisodeVersionDetails>()
            .when((version) => !!version.buzzVersion?.notes && (version.brandings ?? []).length < 1)
            .addWarning((version) => version.buzzVersion?.notes ?? "")
            .when((version) => !version.buzzVersion?.id && !isTrailerVersionType(version.type))
            .addWarning("Version is orphaned")
            .when((version) => !!version.buzzVersion?.isArchived)
            .addWarning("Version is archived")
            .when((version) => isDuplicated(allVersions, version))
            .addWarning((version) => version.buzzVersion?.name ?? "Version is duplicated")
            .validate(versionToValidate);
    };

    const isMatchingVersion = (
        versionToValidate: ContentAssociationTvEpisodeVersionDetails,
        selectedVersions: ContentAssociationTvEpisodeVersionDetails[]
    ) => {
        return selectedVersions.some((version) => doesVersionMatch(versionToValidate, version));
    };

    const doesVersionMatch = (
        episodeItem: ContentAssociationTvEpisodeVersionDetails,
        secondItem: ContentAssociationTvEpisodeVersionDetails
    ): boolean => {
        if (isTrailerVersionType(episodeItem.type) && isTrailerVersionType(secondItem.type)) {
            return true;
        }

        return (
            episodeItem.type.id === secondItem.type.id && episodeItem.brandings?.length === secondItem.brandings?.length
        );
    };

    return {
        isMatchingVersion,
        validateEpisode,
        validateVersion
    };
};

const isDuplicated = (
    allVersions: ContentAssociationTvEpisodeVersionDetails[],
    versionToValidate: ContentAssociationTvEpisodeVersionDetails
) => {
    return (
        allVersions.filter(
            (version: ContentAssociationTvEpisodeVersionDetails) =>
                version.type.id === versionToValidate.type.id &&
                version.distributor.id === versionToValidate.distributor.id &&
                brandingsEqual(versionToValidate.brandings, version.brandings)
        ).length > 1
    );
};

const brandingsEqual = (brandings1?: CodedProperty[], brandings2?: CodedProperty[]) => {
    const brandings1Ids = brandings1?.map((brand: CodedProperty) => brand.id) ?? [];
    const brandings2Ids = brandings2?.map((brand: CodedProperty) => brand.id) ?? [];

    return stringArraysEqual(brandings1Ids, brandings2Ids);
};
